import { QueryClientProvider, QueryClientProviderProps, useQueryClient } from '@tanstack/react-query'
import React, { use, ElementType, ReactNode } from 'react'
import { TestIntlProviderProps } from '~/common/localizationTestingUtils'
import { isNextJsPage } from '~/common/nextJsUtils'
import { queryClient } from '~/common/react-query/queryClient'
import { IntlProviderFromApi, useAlreadyWithinIntlProvider } from '~/components/IntlProviders'
import { PageErrorBoundary } from '~/components/v2/ErrorBoundary'
import { ChakraProviderProps, useTheme } from '~/design_system'
import { ModalContext, ModalProvider } from '~/hooks/useModal'
import { ThemedChakraProvider } from '~/theme/ThemedChakraProvider'
import { useAddInputModalityBodyAttribute } from '~/common/domUtils'

function useAlreadyWithinQueryClientProvider() {
  let alreadyWithinQueryClientProvider: boolean = false
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    alreadyWithinQueryClientProvider = Boolean(useQueryClient())
    // eslint-disable-next-line no-empty
  } catch {}
  return alreadyWithinQueryClientProvider
}

function useAlreadyWithChakraProvider() {
  return Object.keys(useTheme()).length > 0
}

const useAlreadyWithinModalProvider = () => {
  return use(ModalContext)
}

/**
 * The whole point of rendering the providers in this way is so that we can conditionally render them based on whether or not they are already being rendered
 * higher up in the component tree. Additionally, care is taken to not render anything at all so we don't add noise to the React component tree either.
 *
 * That's why it's sorta ugly the way it's done.
 */
export const useRenderMaybeProviders = () => {
  const shouldRenderQueryClientProvider = !useAlreadyWithinQueryClientProvider()
  const shouldRenderChakraProvider = !useAlreadyWithChakraProvider()
  const shouldRenderModalProvider = !useAlreadyWithinModalProvider()
  const shouldRenderIntlProvider = !useAlreadyWithinIntlProvider()

  const chakra = renderChakraProvider(shouldRenderChakraProvider)
  const query = renderQueryClientProvider(shouldRenderQueryClientProvider)
  const modal = renderModalProvider(shouldRenderModalProvider)
  const intl = renderIntlProvider(shouldRenderIntlProvider)

  useAddInputModalityBodyAttribute()

  return {
    all: ({
      children,
      chakraProviderProps,
      reactQueryClientProviderProps,
      intlProviderProps,
      TestIntlProvider
    }: {
      children: ReactNode
      chakraProviderProps?: Partial<ChakraProviderProps>
      reactQueryClientProviderProps?: Partial<QueryClientProviderProps>
      intlProviderProps?: Partial<TestIntlProviderProps>
      TestIntlProvider?: ElementType
    }) =>
      query({
        ...reactQueryClientProviderProps,
        children: chakra({
          ...chakraProviderProps,
          children: intl({
            TestIntlProvider,
            ...intlProviderProps,
            children: modal({
              children: <PageErrorBoundary>{children}</PageErrorBoundary>
            })
          })
        })
      }),
    chakra,
    query,
    modal
  }
}

// eslint-disable-next-line react/display-name
export const renderChakraProvider = (shouldRender: boolean) => (props: Partial<ChakraProviderProps>) => {
  // resetScope="html" is necessary to give the reset CSS a higher specificity than some of the legacy Django page CSS
  return shouldRender ? <ThemedChakraProvider resetScope="html" {...props} /> : props.children
}

// eslint-disable-next-line react/display-name
export const renderQueryClientProvider = (shouldRender: boolean) => (props: Partial<QueryClientProviderProps>) => {
  return shouldRender ? <QueryClientProvider client={queryClient} {...props} /> : props.children
}

export const renderModalProvider =
  (shouldRender: boolean) =>
  // eslint-disable-next-line react/display-name
  ({ children }: { children: ReactNode }) => {
    return shouldRender ? <ModalProvider>{children}</ModalProvider> : children
  }

export const renderIntlProvider =
  (shouldRender: boolean) =>
  // eslint-disable-next-line react/display-name
  ({
    TestIntlProvider,
    ...props
  }: Partial<TestIntlProviderProps> & {
    children: TestIntlProviderProps['children']
    TestIntlProvider?: ElementType
  }) => {
    const IntlProvider = TestIntlProvider ?? IntlProviderFromApi
    return shouldRender ? <IntlProvider {...props} /> : props.children
  }

/**
 * Official way to wrap your components with all the necessary VNDLY application providers
 *
 * Wraps your component with all the necessary providers including:
 *  - ChakraProvider
 *  - QueryClientProvider
 *  - IntlProvider
 *  - ModalProvider
 *  - ErrorPageBoundary
 *
 *  Not included:
 *  - StrictMode
 *  - GlobalCSSVariables
 */
export const AllProviders = ({
  children,
  chakraProviderProps,
  reactQueryClientProviderProps,
  intlProviderProps,
  TestIntlProvider
}: {
  children: ReactNode
  chakraProviderProps?: Partial<ChakraProviderProps>
  reactQueryClientProviderProps?: Partial<QueryClientProviderProps>
  intlProviderProps?: Partial<TestIntlProviderProps>
  TestIntlProvider?: ElementType
}) => {
  const renderProviders = useRenderMaybeProviders()

  // Next.js already wraps the whole page in all of these providers, so we can return early if we are on a next.js page.
  if (isNextJsPage()) return <>{children}</>

  return renderProviders.all({
    chakraProviderProps,
    reactQueryClientProviderProps,
    intlProviderProps,
    TestIntlProvider,
    children: <PageErrorBoundary>{children}</PageErrorBoundary>
  })
}
